import {
  LIST_ALERTS_MANAGEMENT,
  OrderByInput,
  useAlertsManagementListFilter,
  useAlertsManagementListOrder,
  useCurrentWorkspace,
  useSetAlertsManagementListOrder,
} from '@gripp/shared-logic';
import { ColumnDef } from '@tanstack/react-table';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfiniteDataGrid } from '../infiniteDataGrid';
import { AlertsManagementTableData, useColumns } from './useColumns';

export type AlertsManagementListProps = {
  maxHeight?: number;
  paddingBottom?: number;
  preserveScrollKey?: string;
  editAlertMenu: (props: {
    alertId: string;
    label: string;
  }) => React.ReactElement;
  onRowClick: (alert: AlertsManagementTableData) => void;
};

export const AlertsManagementList = ({
  maxHeight,
  paddingBottom,
  preserveScrollKey,
  editAlertMenu,
  onRowClick,
}: AlertsManagementListProps) => {
  const { t } = useTranslation();

  const EditAlertMenu = editAlertMenu;

  const { data } = useAlertsManagementListFilter();
  const { data: orderData } = useAlertsManagementListOrder();
  const { mutateAsync: setOrder } = useSetAlertsManagementListOrder();
  const workspace = useCurrentWorkspace(true);

  const setOrderData = useCallback(
    (order: OrderByInput) => {
      setOrder(order);
    },
    [setOrder]
  );

  const [filter, setFilter] = useState<any>(undefined);
  const [columns, setColumns] = useState<string[]>([]);
  const [alertsSelected, setAlertsSelected] = useState<string[] | undefined>(
    undefined
  );

  const showAlertMenu = useMemo(() => {
    return alertsSelected?.length === 1 ? true : false;
  }, [alertsSelected]);

  const updateAlertSelection = (rows: any) => {
    setAlertsSelected(rows);
  };

  useEffect(() => {
    if (!data || workspace === undefined) return;
    setColumns(data.columns || []);

    const f = { ...data };
    f.workspaceId = workspace?.id;
    if (f.keyword === '') delete f.keyword;
    delete f.columns;
    setFilter(f);
  }, [data, workspace]);

  const filterWithColumns = useMemo(() => {
    return filter ? { ...filter, columns } : undefined;
  }, [filter, columns]);

  const columnsWithFilter = useColumns(filterWithColumns) as ColumnDef<
    any,
    any
  >[];

  const RenderHeader = () => {
    const selectedId =
      showAlertMenu && alertsSelected ? alertsSelected[0] : undefined;
    return selectedId ? (
      <EditAlertMenu
        alertId={selectedId}
        label={t('alertsManagement.menu.editAlert')}
      />
    ) : (
      <></>
    );
  };

  return !filter ? null : (
    <InfiniteDataGrid
      modelName="workflow"
      query={LIST_ALERTS_MANAGEMENT}
      orderBy={orderData}
      filter={filter}
      pageSize={50}
      columns={columnsWithFilter}
      listMaxHeight={maxHeight as number}
      paddingBottom={paddingBottom as number}
      textEmpty={t('alertsManagement.common.textEmpty')}
      dataPrefix="workflow.listAlerts"
      enableScrollRefresh={true}
      preserveScrollKey={preserveScrollKey}
      showScrollIndicator={true}
      exportFileName="alerts"
      updateRowsSelected={(rows) => updateAlertSelection(rows)}
      headerContent={RenderHeader()}
      onOrderByChange={setOrderData}
      onRowClick={(r) => onRowClick(r)}
    />
  );
};
